/**
 * Generic LoRaWAN adapter — IOT-ADAPTER-LIBRARY-1.
 *
 * Tier: aggregator (protocol-level adapter; works with any LoRaWAN-class device
 * routed through a Network Server such as ChirpStack, The Things Network,
 * Loriot, Senet, or AWS IoT for LoRaWAN)
 * Category: asset_tracker, ambient
 *
 * The "Switzerland strategy" exemplar for low-power wide-area sensors. Any
 * LoRaWAN end-device — Sensoterra, Adeunis, Dragino, Browan, Milesight,
 * Decentlab, etc. — can be ingested via the configured Network Server's
 * uplink-webhook, normalised through tenant-configured CayenneLPP /
 * vendor-specific decoders, and surfaced as canonical SensorType events.
 *
 * TYPE-ONLY for this session: capability matrix declared; decoder/transport
 * deferred to dedicated integration session.
 *
 * Patent anchor: F-IOT-ADAPTER-001 (hardware-agnostic IoT integration).
 */

import { NotImplementedAdapter } from '../NotImplementedAdapter.js';

const GENERIC_LORAWAN_CAPABILITIES = {
  configKeys: {
    network_server_url: {
      pushable: false,
      readable: true,
      requiresActivation: true,
      description: 'LoRaWAN Network Server webhook URL (ChirpStack / TTN / Loriot / Senet / AWS IoT)',
    },
    dev_eui: {
      pushable: false,
      readable: true,
      requiresActivation: true,
      description: 'Device EUI (64-bit hex); set at OTAA join provisioning',
    },
    join_eui: {
      pushable: false,
      readable: true,
      requiresActivation: true,
      description: 'Join EUI / AppEUI (64-bit hex); provisioning-time only',
    },
    spreading_factor: {
      pushable: true,
      readable: true,
      enum: ['SF7', 'SF8', 'SF9', 'SF10', 'SF11', 'SF12'],
      requiresActivation: false,
      description: 'LoRa spreading factor (controls range / power / data rate trade-off)',
    },
    decoder_profile: {
      pushable: true,
      readable: true,
      enum: ['cayenne_lpp', 'sensoterra', 'adeunis', 'dragino', 'browan', 'milesight', 'decentlab', 'custom'],
      requiresActivation: false,
      description: 'Payload decoder profile applied by the adapter on uplink',
    },
  },
  telemetryTypes: [
    'temperature_c', 'humidity_pct', 'pressure_hpa', 'co2_ppm', 'light_lux',
    'door_open_bool', 'gps_position', 'battery_voltage_v', 'battery_soc_pct',
  ],
  alertingMode: 'cloud',
  otaSupported: false,
  attestationSupported: false,
  rollbackSupported: false,
  pushLatencyExpectedMs: 60_000,
};

export class GenericLorawanAdapter extends NotImplementedAdapter {
  constructor(device) {
    super(device, 'generic_lorawan', GENERIC_LORAWAN_CAPABILITIES);
  }
}

export function createGenericLorawanAdapterFactory() {
  return (device) => new GenericLorawanAdapter(device);
}
